import { featureMapForSpecimen } from './specimenFeatures';
import type { DichotomousKey, DichotomousKeyNode, KeyCondition } from './dichotomousKey';

function displayValue(value: string | number | boolean): string {
  if (typeof value === 'boolean') return value ? 'present' : 'absent';
  return String(value).replaceAll('-', ' ');
}

/** Builds a logically binary, feature-driven key for a supplied specimen set. */
export function buildFeatureKey(specimenIds: string[], title = 'Feature-based specimen key', preferredFeatureId?: string, preferredValue?: string | number | boolean): DichotomousKey {
  if (specimenIds.length < 2) throw new RangeError('A dichotomous key needs at least two specimens.');
  if (new Set(specimenIds).size !== specimenIds.length) throw new RangeError('Specimen IDs in a key must be unique.');
  const featureMaps = Object.fromEntries(specimenIds.map((id) => [id, featureMapForSpecimen(id)]));
  const nodes: Record<string, DichotomousKeyNode> = {};
  let nextNode = 0;

  const makeBranch = (remaining: string[]): string => {
    if (remaining.length === 1) {
      const id = `terminal-${nextNode++}`;
      nodes[id] = { id, type: 'terminal', specimenId: remaining[0] };
      return id;
    }

    const sharedFeatures = Object.keys(featureMaps[remaining[0]]).filter((featureId) => remaining.every((specimenId) => featureId in featureMaps[specimenId]));
    const candidates: Array<{ featureId: string; value: string | number | boolean; yes: string[]; no: string[]; balance: number }> = [];
    sharedFeatures.forEach((featureId) => {
      const values = Array.from(new Set(remaining.map((specimenId) => featureMaps[specimenId][featureId])));
      values.forEach((value) => {
        const yes = remaining.filter((specimenId) => featureMaps[specimenId][featureId] === value);
        const no = remaining.filter((specimenId) => featureMaps[specimenId][featureId] !== value);
        if (yes.length && no.length) candidates.push({ featureId, value, yes, no, balance: Math.abs(yes.length - no.length) });
      });
    });
    candidates.sort((a, b) => Number(b.featureId === preferredFeatureId) - Number(a.featureId === preferredFeatureId) || Number(b.value === preferredValue) - Number(a.value === preferredValue) || a.balance - b.balance || a.featureId.localeCompare(b.featureId));
    const best = candidates[0];
    if (!best) throw new RangeError(`No shared observable feature can separate these specimens: ${remaining.join(', ')}.`);

    const yesNextId = makeBranch(best.yes);
    const noNextId = makeBranch(best.no);
    const id = `couplet-${nextNode++}`;
    const condition: KeyCondition = { featureId: best.featureId, operator: 'equals', value: best.value };
    const readableFeature = best.featureId.replaceAll(/([A-Z])/g, ' $1').toLowerCase();
    nodes[id] = {
      id,
      type: 'couplet',
      statementYes: `${readableFeature} is ${displayValue(best.value)}`,
      statementNo: `${readableFeature} is not ${displayValue(best.value)}`,
      condition,
      yesNextId,
      noNextId,
    };
    return id;
  };

  const rootNodeId = makeBranch(specimenIds);
  return { id: `key-${Date.now()}`, title, rootNodeId, nodes };
}
